import BaranDataGrid from '../../common/BaranDataGrid.tsx';
import { ChangeEvent, useEffect, useState } from 'react';
import axios from 'axios';
import IResponse, { IPagination } from '../../../interfaces/response.ts';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../../constants/numbers.ts';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import { Button, IconButton, Switch, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { LoadingButton } from '@mui/lab';
import BasicModal from '../../common/BasicModal.tsx';
import { useDebounce } from 'use-debounce';
import { OpenInNewRounded } from '@mui/icons-material';
import { IBlock } from '../../../interfaces/block.ts';
import EditIcon from '@mui/icons-material/Edit';

const headerClassNames = 'text-xs bg-background-paper';

export default function List({ isStodio }: { isStodio?: true }) {
	const { t } = useTranslation();

	const navigate = useNavigate();

	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(true);
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [fetchedData, setFetchedData] = useState<IBlock[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [refresh, setRefresh] = useState(1);
	const [submitRoleLoading, setSubmitRoleLoading] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedBlock, setSelectedBlock] = useState<IBlock>();
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);

	useEffect(() => {
		setLoading(true);
		axios({
			url: isStodio ? `/admin/studio-blocks` : `/admin/block`,
			method: 'GET',
			params: {
				page: page > 0 ? page : 1,
				limit: pageSize,
				search: search,
				// user_id todo
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [page, pageSize, refresh, isStodio]);

	const handleDeleteBlock = () => {
		setSubmitRoleLoading(true);
		axios<IResponse<any>>({
			url: `/admin/block/${selectedBlock!.id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					handleCloseDeleteDialog();
					setRefresh(prev => prev + 1);
				}
			})
			.finally(() => {
				setSubmitRoleLoading(false);
			});
	};

	const handleGoToCreate = () => {
		navigate(isStodio ? '/handle-studios/create' : '/block/create');
	};

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

	const handleShowDeleteDialog = (block: IBlock) => async () => {
		setOpenDeleteDialog(() => {
			setSelectedBlock(block);
			return true;
		});
	};

	const handleEditBlock = (id?: number) => () => {
		navigate(isStodio ? `/handle-studios/edit/${id}` : `/block/edit/${id}`);
	};

	const handleChangeStatus = (row: IBlock) => () => {
		axios({
			url: `/admin/block/${row?.id}/updateStatus`,
			method: 'PUT',
			data: {
				status: row?.status ? 0 : 1,
			},
			loading: true,
		}).then(res => {
			if (res?.data?.success) {
				setFetchedData(p => p?.map(i => (i?.id === row?.id ? { ...i, status: i?.status ? 0 : 1 } : i)));
			}
		});
	};

	const columns: GridColDef<IBlock>[] = [
		{
			field: 'id',
			headerName: t('id'),
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'section',
			headerName: t('section'),
			minWidth: 200,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'title',
			headerName: t('title'),
			flex: 1,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
		},
		{
			field: 'status',
			headerName: t('status'),
			flex: 1,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return <Switch defaultChecked={params?.value} onChange={handleChangeStatus(params.row)} />;
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return (
					<>
						<Link to={params.row?.link ?? ''} target={'_blank'}>
							<IconButton>
								<OpenInNewRounded className="text-text-secondary" />
							</IconButton>
						</Link>
						<IconButton onClick={handleEditBlock(params.row?.id)}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleShowDeleteDialog(params.row)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	return (
		<div className={'w-full'}>
			<BaranDataGrid<IBlock> checkboxSelection={true} loading={loading} pagination={pagination} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page > 0 ? page : 1} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={handleGoToCreate} actionButtonLable={t('add_new_block')} />

			<BasicModal
				open={openDeleteDialog}
				onClose={handleCloseDeleteDialog}
				title={t('delete_block')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={submitRoleLoading} onClick={handleDeleteBlock} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_block_description', { block: selectedBlock?.title })}</Typography>
			</BasicModal>
		</div>
	);
}
